import React, { useState } from "react";
import "./ContactPage.css";
import { contactAPI } from "../services/api";
import useRevealOnIntersect from "../hooks/useRevealOnIntersect";
import { useScrollToTopOnMount } from "../hooks/useScrollToTop";

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useScrollToTopOnMount();
  useRevealOnIntersect(".cp-reveal", { threshold: 0.2 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Store form reference before async operation
    const form = e.currentTarget;

    try {
      const formData = Object.fromEntries(new FormData(form).entries());
      const result = await contactAPI.submitContactForm(formData);
      
      if (result.success || result.fallback) {
        setSubmitted(true);
        // Use stored form reference
        if (form) {
          form.reset();
        }
        
        // Show different message for fallback
        if (result.fallback) {
          setTimeout(() => setSubmitted(false), 15000); // Longer for fallback message
        } else {
          setTimeout(() => setSubmitted(false), 8000);
        }
      } else {
        setError(result.message || 'Failed to send message. Please try again or contact us directly.');
      }
    } catch (error) {
      console.error('Contact form submission failed:', error);
      setError(error.message || 'Failed to send message. Please contact us directly at info.nexite@gmail.com or call +92 323 5522788.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const socials = [
    { key: "linkedin", label: "LinkedIn", icon: "in", href: undefined },
    { key: "twitter", label: "Twitter", icon: "𝕏", href: undefined },
    { key: "facebook", label: "Facebook", icon: "f", href: undefined },
  ];

  return (
    <main className="contact-page">
      <header className="cp-hero">
        <div className="cp-hero-bg" aria-hidden="true">
          <span className="cp-blob b1" />
          <span className="cp-blob b2" />
          <span className="cp-blob b3" />
          <span className="cp-grid-glow" />
        </div>
        <div className="cp-container">
          <h1 className="cp-reveal" data-reveal="up" style={{ "--d": "60ms" }}>
            Let’s build what’s next — together
          </h1>
          <p className="cp-subtitle cp-reveal" data-reveal="up" style={{ "--d": "140ms" }}>
            Tell us about your goals. We’ll respond with a clear, actionable plan.
          </p>
          <div className="cp-hero-actions cp-reveal" data-reveal="up" style={{ "--d": "220ms" }}>
            <a href="#contact-form" className="cp-btn cp-btn-primary cp-btn-3d">
              <span className="shine" aria-hidden="true" />
              Start a conversation
            </a>
            <a href="#details" className="cp-btn cp-btn-secondary">See details</a>
          </div>
        </div>
        <div className="cp-hero-accent" aria-hidden="true" />
      </header>

      <section className="cp-section" id="details">
        <div className="cp-container">
          <div className="cp-tiles">
            <div className="cp-tile cp-reveal" data-reveal="up">
              <div className="cp-tile-icon">📞</div>
              <div className="cp-tile-body">
                <h3>Call us</h3>
                <p>+92 323 5522788</p>
                <a className="cp-link" href="tel:+923235522788">Tap to call</a>
              </div>
              <span className="cp-shine" />
            </div>

            <div className="cp-tile cp-reveal" data-reveal="up" style={{ "--d": "80ms" }}>
              <div className="cp-tile-icon">✉️</div>
              <div className="cp-tile-body">
                <h3>Email</h3>
                <p>info.nexite@gmail.com</p>
                <a className="cp-link" href="mailto:info.nexite@gmail.com">Compose email</a>
              </div>
              <span className="cp-shine" />
            </div>

            <div className="cp-tile cp-reveal" data-reveal="up" style={{ "--d": "160ms" }}>
              <div className="cp-tile-icon">📍</div>
              <div className="cp-tile-body">
                <h3>Address</h3>
                <p>Gulberg Greens, Islamabad</p>
                <a
                  className="cp-link"
                  href="https://maps.google.com/?q=Gulberg Greens, Islamabad"
                  target="_blank"
                  rel="noreferrer"
                >
                  View on Maps
                </a>
              </div>
              <span className="cp-shine" />
            </div>

            <div className="cp-tile cp-reveal" data-reveal="up" style={{ "--d": "240ms" }}>
              <div className="cp-tile-icon">⏰</div>
              <div className="cp-tile-body">
                <h3>Hours</h3>
                <p>Mon–Fri · 9:00–18:00 PKT</p>
                <span className="cp-note">Responses within 1 business day</span>
              </div>
              <span className="cp-shine" />
            </div>
          </div>
        </div>
      </section>

      <section className="cp-section">
        <div className="cp-container cp-grid">
          <article className="cp-card cp-reveal" id="contact-form" data-reveal="left">
            <h2>Send us a message</h2>
            <p className="cp-muted">We’ll get back within one business day.</p>

            {submitted && (
              <div className="cp-alert success">
                <span>✓</span>
                <div>
                  <strong>Message sent.</strong> Thank you! We'll respond shortly.
                </div>
              </div>
            )}

            {error && (
              <div className="cp-alert error">
                <span>⚠</span>
                <div>
                  <strong>Error:</strong> {error}
                </div>
              </div>
            )}

            <form className="cp-form" onSubmit={handleSubmit}>
              <div className="cp-form-row">
                <div className="cp-field">
                  <input type="text" name="name" id="cp_name" required placeholder=" " autoComplete="name" />
                  <label htmlFor="cp_name">Your Name</label>
                </div>
                <div className="cp-field">
                  <input type="email" name="email" id="cp_email" required placeholder=" " autoComplete="email" />
                  <label htmlFor="cp_email">Your Email</label>
                </div>
              </div>

              <div className="cp-form-row">
                <div className="cp-field">
                  <input type="tel" name="phone" id="cp_phone" placeholder=" " autoComplete="tel" />
                  <label htmlFor="cp_phone">Phone (optional)</label>
                </div>

                {/* NOTE: cp-select forces the label to float so it never overlaps the value */}
                <div className="cp-field cp-select">
                  <select name="topic" id="cp_topic" defaultValue="General Inquiry">
                    <option>General Inquiry</option>
                    <option>Security</option>
                    <option>Network</option>
                    <option>IT Infrastructure</option>
                    <option>General Order Supply</option>
                    <option>Partnership</option>
                  </select>
                  <label htmlFor="cp_topic">Topic</label>
                </div>
              </div>

              <div className="cp-field">
                <input type="text" name="subject" id="cp_subject" required placeholder=" " />
                <label htmlFor="cp_subject">Subject</label>
              </div>

              <div className="cp-field">
                <textarea name="message" id="cp_message" rows="6" required placeholder=" " />
                <label htmlFor="cp_message">Your Message</label>
              </div>

              <label className="cp-check">
                <input type="checkbox" name="consent" required />
                <span>I agree to be contacted and accept the privacy policy.</span>
              </label>

              <button type="submit" className="cp-btn cp-btn-primary cp-btn-3d" disabled={isSubmitting}>
                <span className="shine" aria-hidden="true" />
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </article>

          <aside className="cp-side cp-reveal" data-reveal="right">
            <div className="cp-card mini">
              <h3>Talk to Sales</h3>
              <p>Planning a new deployment or modernization? Our team can help scope the best path forward.</p>
              <a className="cp-link" href="tel:+923235522788">Call +92 323 5522788</a>
            </div>

            <div className="cp-card mini">
              <h3>Support & SLAs</h3>
              <p>Existing customer? For SLA or incident assistance, include your site and ticket reference.</p>
              <a className="cp-link" href="mailto:info.nexite@gmail.com?subject=Support%20Request">
                Email Support
              </a>
            </div>

            <div className="cp-card map">
              <div className="cp-map-placeholder">
                <div className="cp-map-title">Our Location</div>
                <p>Gulberg Greens, Islamabad</p>
                <a
                  className="cp-btn cp-btn-secondary"
                  href="https://maps.google.com/?q=Gulberg Greens, Islamabad"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in Maps
                </a>
              </div>
            </div>

            <div className="cp-card mini">
              <h3>Follow us</h3>
              <div className="cp-socials">
                {socials.map((s) =>
                  s.href ? (
                    <a
                      key={s.key}
                      className="cp-social"
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                    >
                      {s.icon}
                    </a>
                  ) : (
                    <button
                      key={s.key}
                      type="button"
                      className="cp-social as-button"
                      aria-label={`${s.label} (coming soon)`}
                      title="Coming soon"
                      disabled
                    >
                      {s.icon}
                    </button>
                  )
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="cp-section cp-alt">
        <div className="cp-container">
          <h2 className="cp-reveal" data-reveal="up">FAQs</h2>
          <div className="cp-faq">
            <details className="cp-reveal" data-reveal="up">
              <summary>How soon do you respond?</summary>
              <p>Within one business day. Priority SLA customers may receive faster responses.</p>
            </details>
            <details className="cp-reveal" data-reveal="up" style={{ "--d": "80ms" }}>
              <summary>Can you help with scoping a solution?</summary>
              <p>Yes. Share your goals and constraints, and we’ll recommend a right‑sized architecture.</p>
            </details>
            <details className="cp-reveal" data-reveal="up" style={{ "--d": "160ms" }}>
              <summary>Where do you operate?</summary>
              <p>We support customers across Pakistan and remote engagements worldwide.</p>
            </details>
          </div>
        </div>
      </section>
    </main>
  );
}